import { Component, OnInit, Inject, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { digger } from '@cbre/digger';
import { Tag } from '@shared/components/tags';
import { ColumnModel } from '@shared/models/columns/column-catalog.model';
import { UserInfoResolver } from '@shared/resolvers/user-info.resolver';
import { L10nTranslationService } from 'angular-l10n';
import { EDITING_BOX_RECORDS, EDITING_BOX_SECTIONS } from '@shared/components/editing-box/editing-box-overlay.config';
import { EditingBoxStreamService } from '@shared/components/editing-box/editing-box-stream.service';
import { TagEditorWrapperComponent } from '@shared/components/tag-editor-wrapper/tag-editor-wrapper.component';

/**
 * Tags Overlay for Editing Box form
 *
 * Handles inline tag editing within the editing-box modal/overlay.
 * Uses TagEditorWrapperComponent to handle tag management logic.
 */
@Component({
  selector: 'miq-tags-overlay',
  templateUrl: './tags-overlay.component.html',
  standalone: true,
  imports: [CommonModule, FormsModule, TagEditorWrapperComponent],
})
export class TagsOverlayComponent implements OnInit {
  public section: Record<string, any>;
  public record: Record<string, any>;
  public entityId: number | string;
  public disableEdit: boolean;
  public canEdit = false;
  public tags: Tag[] = [];
  public filterUrl: string;

  private column: ColumnModel;

  constructor(
    private editingBoxStreamService: EditingBoxStreamService,
    public translationService: L10nTranslationService,
    userInfoResolver: UserInfoResolver,
    @Optional() @Inject(EDITING_BOX_SECTIONS) private injectedSections: any[],
    @Optional() @Inject(EDITING_BOX_RECORDS) private injectedRecord: any
  ) {
    userInfoResolver.isTrustedUser().then(trusted => {
      this.canEdit = this.injectedSections[0]?.columns[0]?.editable && trusted;
    });
  }

  ngOnInit(): void {
    this.section = this.injectedSections[0];
    this.record = this.injectedRecord;
    this.entityId = this.record.entity.id;
    // Initialize data if we have section and record
    if (this.section && this.record) {
      this.extractFromSection();
      this.initializeTags();
    }
  }

  /**
   * Handle tags updated from wrapper component
   */
  public onTagsChanged(updatedTags: Tag[]): void {
    this.tags = updatedTags;
    this.pushChangeStream(this.tags);
  }

  /**
   * Extract column information from section
   */
  private extractFromSection(): void {
    const { columns } = this.section;
    const column: ColumnModel = columns[0];
    this.column = column;
    this.filterUrl = column.lookupCategory;
  }

  /**
   * Initialize tags from record data
   */
  private initializeTags(): void {
    const rawTags = digger(this.record, 'user_tag');
    this.tags = this.parseTags(rawTags);
  }

  /**
   * Parse raw tag data into Tag[] format
   */
  private parseTags(value: any): Tag[] {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value.map((tag, _index) => ({
        id: tag.id ?? null,
        name: tag.name ?? String(tag),
      }));
    }
    return [];
  }

  /**
   * Push change stream to register the change and mark clipboard as dirty
   */
  private pushChangeStream(tags: Tag[]): void {
    if (this.column && this.column.parent) {
      // Create a modified column with the section path as the field
      // This ensures ALL downstream code (including clipboard.service.ts and tagProcessor)
      // uses the correct field path instead of the column's original field ('name')
      const modifiedColumn = {
        ...this.column,
        field: 'user_tag', // Override with section path
      };

      this.editingBoxStreamService.pushChangeStream({
        recordId: this.entityId,
        column: modifiedColumn,
        field: 'user_tag',
        value: tags,
      });
    }
  }
}
